import { ArticleFlat } from 'types'
import { CopyToClipboardInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/CopyToClipboardInlineFormatButton/CopyToClipboardInlineFormatButton'
import { editLink } from 'common/Prose/Prompts/LinkPrompt/editLink'
import { EditLinkInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/LinkInlineFormatButton/EditLinkInlineFormatButton'
import { getMarkBoundaries } from 'proseShared/utils/getMarkBoundaries'
import { IframelyData, getURLMetadata } from 'components/Handbook2_0/Sidebar/HandbookItemTypes/getURLMetadata'
import { INLINE_LINK_MENU_PLUGIN_KEY } from './Constants'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { isAlmanacUrl } from 'utils/urlUtils'
import { Public } from 'almanac-ui/icons'
import { removeLink } from 'common/Prose/Prompts/LinkPrompt/removeLink'
import { TurnIntoEmbedButton } from '../../..//common/Prose/components/TextInlineFormatBar/components/Buttons/TurnIntoEmbedButton'

import { type EditorView } from 'prosemirror-view'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { wrapURLWithHandbook } from 'components/Handbook2_0/util'
import * as React from 'react'
import Styles from './InlineLinkMenuContent.module.scss'
import useIsMounted from 'ismounted'

export interface InlineLinkMenuContentProps {
  article: ArticleFlat
  view: EditorView
  currentLink: string
}

export const InlineLinkMenuContent = ({ article, view, currentLink }: InlineLinkMenuContentProps) => {
  const { state } = view
  const nodePos: number = INLINE_LINK_MENU_PLUGIN_KEY.getState(state).nodePos
  const $nodePos = state.doc.resolve(nodePos)
  const linkPosition = getMarkBoundaries($nodePos, state.schema.marks.link)
  const node = state.doc.nodeAt(linkPosition.from)
  const linkMark = node?.marks.find((mark) => mark.type === state.schema.marks.link)
  const [urlMetaData, setUrlMetaData] = useState<IframelyData | null>(null)
  const isMounted = useIsMounted()
  const history = useHistory()

  const isAnchorLink = currentLink.startsWith('#')
  const isInternalLink = React.useMemo(() => isAlmanacUrl(currentLink), [currentLink])
  const linkContent = React.useMemo(() => {
    if (isAnchorLink) {
      return document.getElementById(currentLink.replace('#', ''))?.textContent ?? currentLink
    }
    return urlMetaData?.meta.title ?? currentLink
  }, [currentLink, isAnchorLink, urlMetaData?.meta.title])

  useEffect(() => {
    const fetchData = async () => {
      if (isAnchorLink || currentLink.startsWith('mailto:')) {
        setUrlMetaData(null)
        return
      }
      const meta = await getURLMetadata(currentLink)

      if (meta && 'links' in meta) {
        isMounted.current && setUrlMetaData(meta)
      } else {
        isMounted.current && setUrlMetaData(null)
      }
    }

    fetchData()
  }, [isMounted, currentLink, isAnchorLink])

  const handleVisit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const handbookURL = wrapURLWithHandbook(e.currentTarget.href)

    if (handbookURL) {
      e.preventDefault()
      history.push(new URL(handbookURL).pathname)
    }
  }

  const linkText = view.state.doc.textBetween(linkPosition.from, linkPosition.to)
  const editLinkModalDefaults = {
    query: linkMark?.attrs.href,
    text: linkText,
  }

  return (
    <InlineFormatBar>
      <InlineFormatButton className={Styles.formatBarLink}>
        <a
          href={currentLink}
          rel="noopener noreferrer ugc"
          target={isInternalLink ? '' : '_blank'}
          onClick={handleVisit}
        >
          {urlMetaData?.links?.icon?.[0]?.href ? (
            <img width={16} height={16} src={urlMetaData.links.icon[0].href} alt="Favicon" />
          ) : (
            <Public />
          )}
          <span className={Styles.linkTitle}>{linkContent}</span>
        </a>
      </InlineFormatButton>
      <TurnIntoEmbedButton view={view} node={node} currentLink={currentLink} linkPosition={linkPosition} />
      <CopyToClipboardInlineFormatButton copyText={currentLink} />
      <EditLinkInlineFormatButton
        view={view}
        article={article}
        defaults={editLinkModalDefaults}
        editCallback={editLink({ view, linkPosition })}
        removeLink={removeLink({ view, linkPosition })}
      />
    </InlineFormatBar>
  )
}
